import React from 'react';
import { useLocation } from 'react-router-dom';
import ProductPage from '../components/seo/ProductPage';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/digital-twin' },
  { hreflang: 'en', href: '/en/digital-twin' },
  { hreflang: 'x-default', href: '/digital-twin' },
];

const CONTENT = {
  es: {
    seoTitle: 'Digital Twin | Madrid en 3D para decidir en caliente | Valoriza',
    seoDescription: 'Modelo 3D georreferenciado de Madrid sobre ArcGIS. Integra capas térmicas, arbolado urbano, edificios y zonas de riesgo para planificar en tiempo real.',
    breadcrumbLabel: 'Digital Twin',
    tag: 'Modelado urbano 3D',
    titleMain: 'Digital Twin: Madrid en 3D, para decidir en',
    titleAccent: 'caliente',
    lead: 'Modelo 3D georreferenciado de Madrid sobre ArcGIS. Integra capas térmicas, arbolado urbano, edificios y zonas de riesgo. Sirve para planificar en tiempo real — incluida la prevención en ola de calor.',
    problema: 'La complejidad de las ciudades modernas requiere herramientas avanzadas para la planificación y gestión urbana. La falta de una visión integrada y en tiempo real de los activos urbanos limita la capacidad de anticipar problemas, optimizar recursos y responder eficazmente a emergencias como olas de calor o eventos climáticos extremos.',
    comoFunciona: 'El Digital Twin de Madrid es un modelo 3D georreferenciado construido sobre la plataforma ArcGIS. Integra datos de diversas fuentes: capas térmicas, información del arbolado urbano, datos de edificios y zonas de riesgo. Permite simular escenarios, analizar el impacto de políticas urbanas y planificar intervenciones en tiempo real, mejorando la resiliencia de la ciudad.',
    paraQuien: 'Administraciones públicas, departamentos de urbanismo, planificación urbana y gestión de emergencias. Urbanistas, ingenieros y arquitectos que necesiten una herramienta integral para el diseño y la operación de ciudades inteligentes.',
    stack: ['ArcGIS', 'GIS', 'Modelado 3D', 'Datos urbanos', 'IA predictiva'],
    enlaces: [{ label: 'TrisTrash: visión artificial en Madrid', to: '/tristrash' }, { label: 'Esri (Partner)', to: 'https://www.esri.es/', external: true }],
    ctaText: '¿Tienes un reto parecido?',
  },
  en: {
    seoTitle: 'Digital Twin | Madrid in 3D, decisions in real time | Valoriza',
    seoDescription: 'Georeferenced 3D model of Madrid on ArcGIS. Integrates thermal layers, urban tree cover, buildings and risk zones to plan in real time.',
    breadcrumbLabel: 'Digital Twin',
    tag: '3D urban modeling',
    titleMain: 'Digital Twin: Madrid in 3D, decisions in real',
    titleAccent: 'time',
    lead: 'Georeferenced 3D model of Madrid on ArcGIS. Integrates thermal layers, urban tree cover, buildings and risk zones. Used to plan in real time — including heatwave prevention.',
    problema: 'The complexity of modern cities requires advanced tools for urban planning and management. The lack of an integrated, real-time view of urban assets limits the ability to anticipate problems, optimize resources and respond effectively to emergencies such as heatwaves or extreme weather events.',
    comoFunciona: 'Madrid\u2019s Digital Twin is a georeferenced 3D model built on the ArcGIS platform. It integrates data from multiple sources: thermal layers, urban tree information, building data and risk zones. It allows scenarios to be simulated, the impact of urban policies to be analyzed, and interventions to be planned in real time, improving the city\u2019s resilience.',
    paraQuien: 'Public administrations, urban planning and emergency management departments. Urban planners, engineers and architects who need a comprehensive tool for designing and operating smart cities.',
    stack: ['ArcGIS', 'GIS', '3D modeling', 'Urban data', 'Predictive AI'],
    enlaces: [{ label: 'TrisTrash: computer vision in Madrid', to: '/en/tristrash' }, { label: 'Esri (Partner)', to: 'https://www.esri.es/', external: true }],
    ctaText: 'Do you have a similar challenge?',
  },
};

export default function DigitalTwinPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const c = CONTENT[locale];
  const path = locale === 'en' ? '/en/digital-twin' : '/digital-twin';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Digital Twin',
    description: c.seoDescription,
    url: `https://innovation.valorizasm.com${path}`,
    brand: { '@id': 'https://innovation.valorizasm.com/#organization' },
  };

  return (
    <ProductPage
      testId="digital-twin-page"
      path={path}
      locale={locale}
      alternates={ALTERNATES}
      jsonLd={[jsonLd]}
      problemaLabel={locale === 'en' ? 'The problem' : 'El problema'}
      comoFuncionaLabel={locale === 'en' ? 'How it works' : 'Cómo funciona'}
      cifrasLabel={locale === 'en' ? 'Figures' : 'Cifras'}
      paraQuienLabel={locale === 'en' ? "Who it's for" : 'Para quién'}
      enlacesLabel={locale === 'en' ? 'Links' : 'Enlaces'}
      {...c}
    />
  );
}
